import React, { useState } from 'react';
import { formatINR, formatTime } from 'utils';

interface RightPanelProps {
  metalName: any;
  timer: any;
  summary: any;
  customerHolding: any[];
  selectedProduct: any;
  addToCartWithDigitalQty: (val: number) => Promise<void>;
  setShowDigitalBalanceModal: (val: boolean) => void;
  isMetalBalanceChecked: boolean;
  setIsMetalBalanceChecked: (val: boolean) => void;
  isPayNowDisabled: boolean;
  handlePayNowClick: () => void;
}

const RightPanel: React.FC<RightPanelProps> = ({
  metalName,
  timer,
  summary,
  customerHolding,
  selectedProduct,
  addToCartWithDigitalQty,
  setShowDigitalBalanceModal,
  isMetalBalanceChecked,
  setIsMetalBalanceChecked,
  isPayNowDisabled,
  handlePayNowClick,
}) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>("amount");

  const matched = customerHolding.find(
    (item) => item?.metal === selectedProduct?.metalCode
  );

  const availableBalance = matched
  ? matched.lockQuantity > 0
    ? matched.quantity // lock qty > 0 → show metal balance
    : matched.quantity - Math.abs(matched.lockQuantity) // lock qty < 0 → subtract lock qty
  : 0;

  const usedBalance = parseFloat(summary?.redeemedMetalQty || '0');

  const isDisabled = availableBalance <= 0;

  const toggleAccordion = (section: string) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };


  return (
    <div className='summary-right'>
      <h6 className='order-summary-heading'>Order Summary</h6>
      <hr className='summary-divider' />
      <label
        className={`digital-metal-balance ${isDisabled ? 'disabled' : ''}`}
      >
        <input
          type='checkbox'
          checked={isMetalBalanceChecked}
          disabled={isDisabled}
          onChange={async (e) => {
            const isChecked = e.target.checked;
            if (isChecked) setShowDigitalBalanceModal(true);
            else {
              setIsMetalBalanceChecked(false);
              await addToCartWithDigitalQty(0);
            }
          }}
        />

        <div className='balance-text'>
          {usedBalance > 0 ? (
            <>
              <span className='balance-title'>
                {`Redeeming Digital ${metalName} Balance`}
              </span>
              <span className='balance-sub'>
                {usedBalance.toFixed(4)} g of {availableBalance.toFixed(4)} g
              </span>
            </>
          ) : (
            <span className='balance-title'>
              {`Use Digital ${metalName} Balance (${availableBalance.toFixed(
                4
              )} g)`}
            </span>
          )}
        </div>
      </label>

      {/* Live Price */}
      <div className='live-price-section'>
        <span className='live-btn'>
          <span className='blinking-dot'></span> Live Rate
        </span>
        <span className='live-price'>
          {summary?.unitPrice
            ? `${summary?.unitPrice.toLocaleString('en-IN', {
                style: 'currency',
                currency: 'INR',
              })} / g`
            : 'Loading'}
        </span>

        <span className='live-price-timer'>{formatTime(timer)}</span>
      </div>

      <div className='quotation'>
        {/* Accordion 1 - Item Details */}
        <div className='accordion'>
          <div
            className='accordion-header'
            onClick={() => toggleAccordion('item')}
          >
            <span>{metalName ? metalName : 'Metal'} to Purchase</span>
            <div className='header-right'>
              <span>
                {summary?.sellQuantity
                  ? `${parseFloat(summary?.sellQuantity || 0).toFixed(4)} g`
                  : '0.0000 g'}
              </span>
              <span className='accordion-icon'>
                {openAccordion === 'item' ? (
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <polyline points='18 15 12 9 6 15'></polyline>
                  </svg>
                ) : (
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <polyline points='6 9 12 15 18 9'></polyline>
                  </svg>
                )}
              </span>
            </div>
          </div>

          {openAccordion === 'item' && (
            <div className='accordion-content'>
              {/* Item Quantity */}
              {parseFloat(summary?.sellQuantity || 0) +
                parseFloat(summary?.redeemedMetalQty || 0) >
                0 && (
                <div className='item-row'>
                  <span className='list-name'>Item Quantity</span>
                  <span className='list-value'>
                    {(
                      parseFloat(summary?.sellQuantity || 0) +
                      parseFloat(summary?.redeemedMetalQty || 0)
                    ).toFixed(4)}{' '}
                    g
                  </span>
                </div>
              )}

              {/* Redeemed Quantity */}
              {parseFloat(summary?.redeemedMetalQty || 0) > 0 && (
                <div className='item-row'>
                  {/* <span className='list-name'>Redeemed Quantity</span> */}
                  <span className='list-name'>
                    Digital {metalName ? metalName : 'Metal'} Redeemed
                  </span>
                  <span className='list-value'>
                    {parseFloat(summary?.redeemedMetalQty).toFixed(4)} g
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Accordion 2 - Charges */}
        <div className='accordion'>
          <div
            className='accordion-header'
            onClick={() => toggleAccordion('amount')}
          >
            <span>Amount Payable</span>
            <div className='header-right'>
              <span>
                {formatINR(parseFloat(summary?.deliveryQuotationTotal || 0))}
              </span>
              <span className='accordion-icon'>
                {openAccordion === 'amount' ? (
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <polyline points='18 15 12 9 6 15'></polyline>
                  </svg>
                ) : (
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <polyline points='6 9 12 15 18 9'></polyline>
                  </svg>
                )}
              </span>
            </div>
          </div>

          {openAccordion === 'amount' && (
            <div className='accordion-content'>
              {parseFloat(summary?.sellTotalAmount || 0) > 0 && (
                <div className='item-row'>
                  <span className='list-name'>{metalName} Purchase</span>
                  <span className='list-value'>
                    {formatINR(parseFloat(summary.sellTotalAmount))}
                  </span>
                </div>
              )}

              {parseFloat(summary?.sellTotalTax || 0) > 0 && (
                <div className='item-row'>
                  <span className='list-name'>GST on {metalName} (3%)</span>
                  <span className='list-value'>
                    {formatINR(parseFloat(summary.sellTotalTax))}
                  </span>
                </div>
              )}

              {(parseFloat(summary?.deliveryPremiumRate || 0) > 0 ||
                parseFloat(summary?.deliveryTotalTax || 0) > 0) && (
                <div className='item-row'>
                  <span className='list-name'>Making + Delivery</span>
                  <span className='list-value'>
                    {formatINR(
                      parseFloat(summary?.deliveryPremiumRate || 0) +
                        parseFloat(summary?.deliveryTotalTax || 0)
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <hr className='summary-divider' />

      {/* Pay Now */}
      <button
        className='pay-now-btn'
        disabled={isPayNowDisabled}
        onClick={handlePayNowClick}
      >
        Pay Now
      </button>
    </div>
  );
};

export default RightPanel;
